import React from 'react'
import {
  Typography,
  IconButton,
  List,
  ListItem,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material'
import {
  Close,
  NotificationsNone,
  CheckCircle,
  Warning,
  Error,
  Info,
  Delete,
  OpenInNew,
} from '@mui/icons-material'
import { useIsMobile } from 'lib/utils/mediaQueries'

// Types
import {
  NotificationPopupProps,
  NotificationItemProps,
} from './NotificationPopup.types'

// Logic
import { useNotificationPopupLogic } from './NotificationPopup.logic'

// Styles
import styles from './notificationPopup.module.scss'

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification: {
    id,
    title,
    message,
    type,
    actionUrl,
    actionText,
    canDismiss = false,
  },
}) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle fontSize="small" color="success" />
      case 'warning':
        return <Warning fontSize="small" color="warning" />
      case 'error':
        return <Error fontSize="small" color="error" />
      case 'info':
      default:
        return <Info fontSize="small" color="info" />
    }
  }

  return (
    <ListItem
      className={styles.notificationItem}
      onClick={(e) => {
        if (actionUrl) {
          e.preventDefault()
          e.stopPropagation()
          window.location.href = actionUrl
        }
      }}
    >
      <div className={styles.notificationItemContent}>
        <div className={styles.notificationHeader}>
          <div className={styles.notificationTitleWrapper}>
            {getTypeIcon(type)}
            <Typography className={styles.notificationTitle}>
              {title}
            </Typography>
          </div>
          <div className={styles.notificationActions}>
            {actionUrl && (
              <IconButton
                className={styles.actionButton}
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  window.open(actionUrl, '_blank')
                }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            )}
            {canDismiss && (
              <IconButton
                className={styles.actionButton}
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  // To be used when needed
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>

        <Typography className={styles.notificationMessage}>
          {message}
        </Typography>

        <div className={styles.notificationMeta}>
          {actionText && actionUrl && (
            <Button
              variant="outlined"
              size="small"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                window.location.href = actionUrl
              }}
            >
              {actionText}
            </Button>
          )}
        </div>
      </div>
    </ListItem>
  )
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({
  isOpen,
  onClose,
  notifications = [],
  isLoading = false,
  emptyStateMessage = 'No notifications',
  filterPeriod = '7days',
  onFilterChange,
}) => {
  const isMobile = useIsMobile()

  const { hasNotifications, formatDate, groupNotificationsByDate } =
    useNotificationPopupLogic({
      isOpen,
      onClose,
      notifications,
      isLoading,
      emptyStateMessage,
      filterPeriod,
    })

  if (!isOpen) return null

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingState}>
          <CircularProgress size={32} />
        </div>
      )
    }

    if (!hasNotifications) {
      return (
        <div className={styles.emptyState}>
          <NotificationsNone className={styles.emptyIcon} />
          <Typography className={styles.emptyText}>
            {emptyStateMessage}
          </Typography>
        </div>
      )
    }

    const groupedNotifications = groupNotificationsByDate(notifications)

    return (
      <List className={styles.notificationList}>
        {groupedNotifications.map(
          ({ date, notifications: groupNotifications }) => (
            <React.Fragment key={date.toISOString()}>
              {filterPeriod === '7days' && (
                <ListItem className={styles.dateHeader}>
                  <Typography variant="subtitle1" className={styles.dateText}>
                    {formatDate(date)}
                  </Typography>
                </ListItem>
              )}
              {groupNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                />
              ))}
            </React.Fragment>
          )
        )}
      </List>
    )
  }

  const popupContent = (
    <div className={styles.popupContainer}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <Typography variant="h4">
            Action Required: Procedures in Progress ({notifications.length})
          </Typography>
          {onFilterChange && (
            <FormControl size="small" className={styles.filterSelect}>
              <Select
                size="small"
                value={filterPeriod}
                onChange={(e) =>
                  onFilterChange(e.target.value as 'today' | '7days')
                }
                className={styles.filterSelect}
                sx={{
                  color: isMobile ? 'white' : 'inherit',
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="7days">Last 7 Days</MenuItem>
              </Select>
            </FormControl>
          )}
        </div>
        <div className={styles.actions}>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            size="small"
          >
            <Close />
          </IconButton>
        </div>
      </div>

      <div className={styles.content}>{renderContent()}</div>
    </div>
  )

  return (
    <div className={styles.notificationPopup}>
      {isMobile && <div className={styles.backdrop} onClick={onClose} />}
      {popupContent}
    </div>
  )
}

export default NotificationPopup
